import { useState, FormEvent } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { Lock, Loader2, Check, XCircle, ArrowLeft } from 'lucide-react';
import { setPasswordViaInvite, resetPassword, ApiException } from '../api/client';

/**
 * Gemeinsame Seite fuer:
 * - /set-password?token=... (Invite)
 * - /reset-password?token=... (Passwort-Reset)
 */
export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get('token');
  const isReset = location.pathname === '/reset-password';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwoerter stimmen nicht ueberein');
      return;
    }

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    if (!token) {
      setError('Kein Token vorhanden');
      return;
    }

    setLoading(true);

    try {
      if (isReset) {
        await resetPassword(token, password);
      } else {
        await setPasswordViaInvite(token, password);
      }
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error || 'Fehler beim Setzen des Passworts');
      } else {
        setError('Verbindungsfehler');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Ungueltiger Link</h1>
          <p className="text-slate-500 mb-6">
            Dieser Link ist ungueltig. Bitte fordern Sie einen neuen Link an.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900">
            {isReset ? 'Passwort zuruecksetzen' : 'Passwort setzen'}
          </h1>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-slate-700 font-medium mb-2">Passwort gesetzt</p>
            <p className="text-slate-500 text-sm mb-6">
              Ihr Passwort wurde erfolgreich {isReset ? 'zurueckgesetzt' : 'gesetzt'}. Sie koennen sich jetzt anmelden.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 w-full py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-medium transition-colors"
            >
              Zur Anmeldung
            </Link>
          </div>
        ) : (
          <>
            <p className="text-slate-500 text-sm mb-6 text-center">
              {isReset
                ? 'Geben Sie Ihr neues Passwort ein.'
                : 'Willkommen! Bitte setzen Sie Ihr Passwort.'}
            </p>

            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                  Neues Passwort
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Mindestens 8 Zeichen</p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
                  Passwort wiederholen
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-medium transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gespeichert...
                  </span>
                ) : (
                  'Passwort setzen'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
